import type { QueryClient } from '@tanstack/react-query'
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { clearAnalyticsUser, setAnalyticsUser } from '@/core/analytics'
import { API_PATHS } from '@/core/api/api-paths'
import { graphqlClient } from '@/core/api/graphql-client'
import { restClient } from '@/core/api/rest-client'
import { clearUserData } from '@/core/auth/clear-user-data'
import {
  clearSession,
  isRefreshTokenExpired,
  loadSession,
  saveSession,
} from '@/core/auth/session'
import { setSessionInvalidHandler } from '@/core/auth/token-manager'
import { clearSentryUser, setSentryUser } from '@/core/sentry'
import {
  trackAutologinSuccess,
  trackUserHasErrorOnLogin,
  trackUserSignedIn,
} from '@/features/auth/analytics'
import { UserDocument } from '@/generated/graphql'
import type { LoginResponse, User } from './types'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (
    email: string,
    password: string,
    queryClient: QueryClient,
  ) => Promise<void>
  autoLogin: (token: string, queryClient: QueryClient) => Promise<void>
  ssoLogin: (ssoToken: string, queryClient: QueryClient) => Promise<void>
  // Persist a LoginResponse the caller already holds (no POST). Used by the
  // email-confirmed cancellation page, whose confirm call returns the session.
  applySession: (data: LoginResponse, queryClient: QueryClient) => Promise<void>
  logout: (queryClient: QueryClient) => Promise<void>
  /**
   * Local-only teardown: everything logout() does except telling the backend.
   *
   * Used when we deliberately refuse a session we have just minted — the
   * membership login wall. Calling logout() there would POST /auth/logout,
   * which revokes the refresh family the handoff token belongs to; on Android
   * that family is the mobile app's own session, so opening a web screen would
   * sign the user out of the app as a side effect.
   */
  discardSession: (queryClient: QueryClient) => void
  initializeSession: () => void
  clearError: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  immer((set, get) => {
    /**
     * Shared "tokenless credential → full session" flow used by autoLogin and
     * ssoLogin. They both:
     *   1. Drop any prior tab session.
     *   2. POST the credential to the given endpoint with `skipAuth`.
     *   3. Persist the LoginResponse, identify Sentry/Amplitude.
     *   4. Prefetch user data into the TanStack cache.
     *   5. Flip isAuthenticated.
     *
     * The only variation is endpoint + body, so it's parameterised here.
     * NOT used by `login` — that path has loading/error UI state and a
     * stoic-redirect side effect, and doesn't pre-clear localStorage session.
     *
     * If you need per-call-site variation (e.g. different tracking event)
     * don't pile options on this helper — just inline the body again.
     */
    const exchangeForSession = async (
      endpoint: string,
      body: Record<string, unknown>,
      queryClient: QueryClient,
    ): Promise<void> => {
      clearSession()
      clearUserData()
      queryClient.clear()

      const data = await restClient.post<LoginResponse>(endpoint, body, {
        skipAuth: true,
      })

      saveSession({
        accessToken: data.token,
        refreshToken: data.refreshToken,
        expiresAt: data.tokenExpirationDate,
        refreshExpiresAt: data.refreshTokenExpirationDate,
        ssoToken: data.ssoToken ?? null,
      })

      setSentryUser(data.user.id)
      setAnalyticsUser(data.user.id)
      trackAutologinSuccess()

      try {
        await queryClient.fetchQuery({
          queryKey: ['user'],
          queryFn: () => graphqlClient.request(UserDocument),
        })
      } catch {
        // Non-blocking — pages will retry
      }

      set((state) => {
        state.user = data.user
        state.isAuthenticated = true
      })
    }

    // A present-but-stale session blob must NOT read as authenticated — that
    // flag gates queries (me, billing-history, feature flags) and a refresh-
    // expired session would fire them tokenless → backend "No auth token".
    const initialSession = loadSession()

    return {
      user: null,
      isAuthenticated:
        initialSession !== null && !isRefreshTokenExpired(initialSession),
      isLoading: false,
      error: null,

      login: async (email, password, queryClient) => {
        set((state) => {
          state.isLoading = true
          state.error = null
        })

        queryClient.clear()

        try {
          const data = await restClient.post<LoginResponse>(
            API_PATHS.auth.login,
            { email, password },
            { skipAuth: true },
          )

          saveSession({
            accessToken: data.token,
            refreshToken: data.refreshToken,
            expiresAt: data.tokenExpirationDate,
            refreshExpiresAt: data.refreshTokenExpirationDate,
            ssoToken: data.ssoToken ?? null,
          })

          setSentryUser(data.user.id)
          setAnalyticsUser(data.user.id)
          trackUserSignedIn()

          // Prefetch user data into TanStack cache
          // so onboarding/plan page loads instantly without spinner
          try {
            await queryClient.fetchQuery({
              queryKey: ['user'],
              queryFn: () => graphqlClient.request(UserDocument),
            })
          } catch {
            // Non-blocking — pages will retry
          }

          set((state) => {
            state.user = data.user
            state.isAuthenticated = true
            state.isLoading = false
          })
        } catch {
          set((state) => {
            state.isLoading = false
            state.error = 'Wrong email or password'
          })
          trackUserHasErrorOnLogin()
        }
      },

      autoLogin: (token, queryClient) =>
        exchangeForSession(API_PATHS.auth.autoLogin, { token }, queryClient),

      // Mobile-to-web handoff entry point. Mobile clients build
      // /autologin?sso_token=… with a credential cached at login/refresh time
      // (see backend feat/sso-token-mobile-web-handoff). sso_token is
      // multi-use — racing duplicates from two open webviews are fine.
      ssoLogin: (ssoToken, queryClient) =>
        exchangeForSession(API_PATHS.auth.ssoLogin, { ssoToken }, queryClient),

      // Same persistence as exchangeForSession but for a LoginResponse the
      // caller already has (the email-confirmed cancellation's confirm call
      // returns the session inline, so there's no second POST). Inlined per the
      // exchangeForSession note — the variation is "no POST, no autologin event".
      applySession: async (data, queryClient) => {
        clearSession()
        clearUserData()
        queryClient.clear()

        saveSession({
          accessToken: data.token,
          refreshToken: data.refreshToken,
          expiresAt: data.tokenExpirationDate,
          refreshExpiresAt: data.refreshTokenExpirationDate,
          ssoToken: data.ssoToken ?? null,
        })

        setSentryUser(data.user.id)
        setAnalyticsUser(data.user.id)

        try {
          await queryClient.fetchQuery({
            queryKey: ['user'],
            queryFn: () => graphqlClient.request(UserDocument),
          })
        } catch {
          // Non-blocking — pages will retry
        }

        set((state) => {
          state.user = data.user
          state.isAuthenticated = true
        })
      },

      discardSession: (queryClient) => {
        clearSession()
        clearUserData()
        queryClient.clear()
        clearSentryUser()
        clearAnalyticsUser()
        set((state) => {
          state.user = null
          state.isAuthenticated = false
        })
      },

      logout: async (queryClient) => {
        try {
          await restClient.post(API_PATHS.auth.logout)
        } catch {
          // Continue with local cleanup even if server call fails
        }
        // Delegates so the two teardowns cannot drift — logout is exactly
        // discardSession plus telling the backend.
        get().discardSession(queryClient)
      },

      initializeSession: () => {
        const session = loadSession()
        if (session) {
          set((state) => {
            state.isAuthenticated = true
          })
        }
      },

      clearError: () => {
        set((state) => {
          state.error = null
        })
      },
    }
  }),
)

// Mirrors iOS LeaplyApp: for await _ in auth.logoutSignal { startAuthFlow() }
// When token-manager detects invalid session → update store → redirect to login
setSessionInvalidHandler(() => {
  clearUserData()
  clearSentryUser()
  clearAnalyticsUser()
  useAuthStore.setState({ user: null, isAuthenticated: false })
})
